import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ProjectContactFormSchema, projectContactFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ProjectContactForm } from "@/forms/project-contact-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface AddProjectContactModalProps extends ModalProps {
	projectId: string;
	onComplete: () => Promise<void>;
	contactId?: string;
}

export function AddProjectContactModal({
	isOpen,
	onClose,
	projectId,
	onComplete,
	contactId,
}: AddProjectContactModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		if (contactId) {
			const response = await client.projects[":projectId"].contacts[":contactId"].$get({
				param: {
					projectId,
					contactId,
				},
			});
			const data = await responseHandler(response);
			if (data.contact) {
				return {
					contactId: data.contact.id,
					contactMemberIds: data.contact._contactMembers.map((member) => member.id),
				};
			}
		}
		return {} as ProjectContactFormSchema;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(projectContactFormSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ProjectContactFormSchema) {
		try {
			await client.projects[":projectId"].contacts.$post({
				param: {
					projectId,
				},
				json,
			});
			await onComplete();
			onClose();
			methods.reset();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("contacts.add_contact")}
				width="800px"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
				data-cy="add-project-contact-modal"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ProjectContactForm contactIsDisabled={false} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
